import { TEXT_SIZE_XSMALL, Txt, TxtProps } from 'components/Txt';
import { theme } from 'lib/theme';
import * as React from 'react';
import {
  AccessibilityProps,
  GestureResponderEvent,
  ImageStyle,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { Icon, ICONS } from './Icon';
import { Pressable } from './Pressable';
import { isLiquidGlassAvailable } from 'expo-glass-effect';
import { Glass } from './Glass';
import { useIsInGlass } from 'hooks/isInGlass';
import { useIsInHeader } from 'hooks/isInHeader';
import { useIsInButtonGroup } from 'hooks/isInButtonGroup';

export enum ButtonTheme {
  'secondary' = 'secondary',
  'themeless' = 'themeless',
  'header' = 'header',
}

export const BUTTON_BORDER_RADIUS = 99;
export const BUTTON_HEIGHT = 32;
export const BUTTON_HEIGHT_HEADER = 44;
export const BUTTON_COMPACT_HEIGHT = 24;

type OmitStyles =
  | 'margin'
  | 'marginHorizontal'
  | 'marginVertical'
  | 'marginLeft'
  | 'marginRight'
  | 'padding'
  | 'paddingHorizontal'
  | 'paddingVertical'
  | 'paddingLeft'
  | 'paddingRight'
  | 'paddingTop'
  | 'paddingBottom'
  | 'backgroundColor'
  | 'borderColor'
  | 'borderWidth'
  | 'width';

interface ButtonThemeConfig {
  adjustLineHeight: boolean;
  // style: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // styleHovered: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // stylePressed: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // styleDestructive: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // styleHoveredDestructive: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // stylePressedDestructive: StyleProp<Omit<ViewStyle, OmitStyles>>;
  // styleText: StyleProp<TextStyle>;
  textColor: string;
  textColorHovered: string;
  textColorDestructive: string;
  textColorDestructiveHovered: string;
  iconColor: string;
  iconColorHovered: string;
  iconColorDestructive: string;
  iconColorDestructiveHovered: string;
  height: number | undefined;
  heightCompact: number | undefined;
  rounded: boolean;
  paddingLeft: number;
  paddingRight: number;

  borderColor: string;
  borderWidth: number;
  backgroundColor: string;
  backgroundColorHovered: string;
  backgroundColorPressed: string;
  backgroundColorDestructive: string;
  backgroundColorHoveredDestructive: string;
  backgroundColorPressedDestructive: string;
  borderColorDestructive: string;
  glass: boolean;
}

// const fixLineHeight = (lineHeight: number) =>
//   Platform.OS === 'ios'
//     ? lineHeight - 2
//     : Platform.OS === 'android'
//     ? lineHeight - 3
//     : lineHeight - 1;

const themes: Record<ButtonTheme, ButtonThemeConfig> = {
  secondary: {
    glass: true,
    rounded: false,
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    paddingLeft: theme.padding,
    paddingRight: theme.padding,
    //
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.blue80,
    borderWidth: 1,
    backgroundColorHovered: theme.colors.blue10,
    backgroundColorPressed: theme.colors.blue20,
    textColor: theme.colors.blue,
    textColorHovered: theme.colors.blueDark,
    iconColor: theme.colors.blue,
    iconColorHovered: theme.colors.blueDark,
    //
    backgroundColorDestructive: 'white',
    borderColorDestructive: theme.colors.red80,
    backgroundColorHoveredDestructive: theme.colors.red10,
    backgroundColorPressedDestructive: theme.colors.red20,
    textColorDestructive: theme.colors.red,
    textColorDestructiveHovered: theme.colors.red,
    iconColorDestructive: theme.colors.red,
    iconColorDestructiveHovered: theme.colors.red,
  },
  themeless: {
    glass: false,
    rounded: false,
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    paddingLeft: theme.padding,
    paddingRight: theme.padding,
    //
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderWidth: 0,
    backgroundColorHovered: theme.colors.blue10,
    backgroundColorPressed: theme.colors.blue20,
    textColor: theme.colors.blue,
    textColorHovered: theme.colors.blueDark,
    iconColor: theme.colors.blue,
    iconColorHovered: theme.colors.blueDark,
    //
    backgroundColorDestructive: 'transparent',
    borderColorDestructive: 'transparent',
    backgroundColorHoveredDestructive: theme.colors.red10,
    backgroundColorPressedDestructive: theme.colors.red20,
    textColorDestructive: theme.colors.red,
    textColorDestructiveHovered: theme.colors.red,
    iconColorDestructive: theme.colors.red,
    iconColorDestructiveHovered: theme.colors.red,
  },
  header: {
    glass: true,
    rounded: true,
    adjustLineHeight: true,
    height: BUTTON_HEIGHT_HEADER,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    paddingLeft: theme.padding,
    paddingRight: theme.padding,
    //
    backgroundColor: theme.colors.headerButtonBackground,
    borderColor: 'transparent',
    borderWidth: 0,
    backgroundColorHovered: theme.colors.headerButtonBackgroundHover,
    backgroundColorPressed: theme.colors.headerButtonBackgroundPressed,
    textColor: theme.colors.blue,
    textColorHovered: theme.colors.blueDark,
    iconColor: theme.colors.blue,
    iconColorHovered: theme.colors.blueDark,
    //
    backgroundColorDestructive: theme.colors.headerButtonBackground,
    borderColorDestructive: 'transparent',
    backgroundColorHoveredDestructive: theme.colors.red10,
    backgroundColorPressedDestructive: theme.colors.red20,
    textColorDestructive: theme.colors.red,
    textColorDestructiveHovered: theme.colors.red,
    iconColorDestructive: theme.colors.red,
    iconColorDestructiveHovered: theme.colors.red,
  },
};

export type ButtonProps = {
  onPress?: ((event: GestureResponderEvent) => void) | null;
  onPressIn?: (event: GestureResponderEvent) => void;
  onPressOut?: (event: GestureResponderEvent) => void;
  buttonTheme?: ButtonTheme;
  icon?: ICONS;
  iconSize?: number;
  iconStyle?: StyleProp<ImageStyle>;
  iconColor?: string;
  disabled?: boolean;
  chipLike?: boolean;
  iconRight?: ICONS;
  iconRightColor?: string;
  iconRightSize?: number;
  iconRightStyle?: StyleProp<ImageStyle>;
  textStyle?: StyleProp<TextStyle>;
  compact?: boolean;
  text?: string;
  textSize?: TxtProps['size'];
  textWeight?: TxtProps['weight'];
  uppercase?: boolean;
  marginRight?: boolean;
  marginLeft?: boolean;
  paddingLeft?: number;
  paddingRight?: number;
  destructive?: boolean;
  focusOnRender?: boolean;
  /**
   * Overrides the text that's read by the screen reader when the user interacts with the element. By default, the
   * label is constructed by traversing all the children and accumulating all the Text nodes separated by space.
   */
  accessibilityLabel?: AccessibilityProps['accessibilityLabel'];
  /**
   * An accessibility hint helps users understand what will happen when they perform an action on the accessibility element when that result is not obvious from the accessibility label.
   */
  accessibilityHint?: AccessibilityProps['accessibilityHint'];
  adjustsFontSizeToFit?: boolean;
  numberOfLines?: number;
  style?: StyleProp<Omit<ViewStyle, OmitStyles>>;
  styleHovered?: StyleProp<ViewStyle>;
  stylePressed?: StyleProp<ViewStyle>;
  showBadge?: boolean;
  children?: React.ReactNode;
  focusable?: boolean;
  rbs?: boolean;
  borderRadius?: number;
  borderTopLeftRadius?: number;
  borderTopRightRadius?: number;
  borderBottomLeftRadius?: number;
  borderBottomRightRadius?: number;
  glass?: boolean;
  width?: number;
  backgroundColor?: string;
};

export const Button: React.FC<ButtonProps> = ({
  buttonTheme = ButtonTheme.themeless,
  children,
  chipLike,
  compact = false,
  destructive,
  disabled: incomingDisabled,
  focusOnRender,
  icon,
  iconSize = 19,
  iconRight,
  iconRightSize,
  iconRightStyle,
  iconStyle,
  iconColor: incomingIconColor,
  iconRightColor,
  marginLeft = false,
  marginRight = false,
  paddingLeft,
  paddingRight,
  onPress,
  onPressIn,
  onPressOut,
  text,
  textSize = 'small',
  textStyle,
  textWeight = 'bold',
  uppercase = false,
  accessibilityLabel,
  accessibilityHint,
  adjustsFontSizeToFit,
  numberOfLines = 1,
  style,
  styleHovered,
  stylePressed,
  showBadge,
  focusable,
  rbs,
  borderRadius: incomingBorderRadius,
  borderTopLeftRadius: incomingBorderTopLeftRadius,
  borderTopRightRadius: incomingBorderTopRightRadius,
  borderBottomLeftRadius: incomingBorderBottomLeftRadius,
  borderBottomRightRadius: incomingBorderBottomRightRadius,
  glass,
  width,
  backgroundColor: incomingBackgroundColor,
}) => {
  const isInButtonGroup = useIsInButtonGroup();
  const isInGlass = useIsInGlass();
  const isInHeader = useIsInHeader();
  const isGlassAvailable = isLiquidGlassAvailable();

  const themeConfig =
    isInHeader || isInButtonGroup
      ? themes[ButtonTheme.header]
      : buttonTheme === ButtonTheme.themeless
        ? themes[buttonTheme]
        : themes.themeless;

  const buttonText = children || text || undefined;

  const disabled = incomingDisabled || (!onPress && !onPressIn);

  const withGlass =
    glass !== undefined ? glass : isInGlass ? false : themeConfig.glass;

  const borderTopLeftRadius =
    incomingBorderTopLeftRadius ??
    incomingBorderRadius ??
    (chipLike || themeConfig.rounded ? 99 : BUTTON_BORDER_RADIUS);
  const borderTopRightRadius =
    incomingBorderTopRightRadius ??
    incomingBorderRadius ??
    (chipLike || themeConfig.rounded ? 99 : BUTTON_BORDER_RADIUS);
  const borderBottomLeftRadius =
    incomingBorderBottomLeftRadius ??
    incomingBorderRadius ??
    (chipLike || themeConfig.rounded ? 99 : BUTTON_BORDER_RADIUS);
  const borderBottomRightRadius =
    incomingBorderBottomRightRadius ??
    incomingBorderRadius ??
    (chipLike || themeConfig.rounded ? 99 : BUTTON_BORDER_RADIUS);

  const backgroundColor =
    incomingBackgroundColor ??
    ((isGlassAvailable && withGlass) || isInButtonGroup
      ? undefined
      : destructive
        ? themeConfig.backgroundColorDestructive
        : themeConfig.backgroundColor);
  const backgroundColorHovered =
    isGlassAvailable && withGlass
      ? undefined
      : destructive
        ? themeConfig.backgroundColorHoveredDestructive
        : themeConfig.backgroundColorHovered;
  const backgroundColorPressed =
    isGlassAvailable && withGlass
      ? undefined
      : destructive
        ? themeConfig.backgroundColorPressedDestructive
        : themeConfig.backgroundColorPressed;

  const [textColor, setTextColor] = React.useState(
    destructive ? themeConfig.textColorDestructive : themeConfig.textColor,
  );
  const [iconColor, setIconColor] = React.useState(
    destructive ? themeConfig.iconColorDestructive : themeConfig.iconColor,
  );
  const handleHoverIn = React.useCallback(() => {
    setTextColor(
      destructive
        ? themeConfig.textColorDestructive
        : themeConfig.textColorHovered,
    );
    setIconColor(
      destructive
        ? themeConfig.iconColorDestructiveHovered
        : themeConfig.iconColorHovered,
    );
  }, [
    destructive,
    themeConfig.iconColorDestructiveHovered,
    themeConfig.iconColorHovered,
    themeConfig.textColorDestructive,
    themeConfig.textColorHovered,
  ]);
  const handleHoverOut = React.useCallback(() => {
    setTextColor(
      destructive ? themeConfig.textColorDestructive : themeConfig.textColor,
    );
    setIconColor(
      destructive ? themeConfig.iconColorDestructive : themeConfig.iconColor,
    );
  }, [
    destructive,
    themeConfig.iconColor,
    themeConfig.iconColorDestructive,
    themeConfig.textColor,
    themeConfig.textColorDestructive,
  ]);

  return (
    <Glass
      glass={withGlass}
      disabled={disabled}
      style={[
        // !disabled && (onPress || onPressIn)
        //   ? styles.buttonPressable
        //   : styles.disabled,
        {
          borderTopLeftRadius,
          borderTopRightRadius,
          borderBottomLeftRadius,
          borderBottomRightRadius,
          borderColor: destructive
            ? themeConfig.borderColorDestructive
            : themeConfig.borderColor,
          borderWidth: themeConfig.borderWidth,
          height: compact ? themeConfig.heightCompact : themeConfig.height,
          width,
        },
        marginRight ? { marginRight: theme.buttonMargin } : undefined,
        marginLeft ? { marginLeft: theme.buttonMargin } : undefined,
        disabled && !withGlass ? { opacity: theme.opacityDisabled } : undefined,
        style,
      ]}
    >
      <Pressable
        focusable={focusable}
        accessible={focusable}
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        focusOnRender={focusOnRender}
        onPress={onPress || undefined}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled}
        style={[
          {},
          !disabled ? styles.buttonPressable : undefined,
          {
            borderTopLeftRadius,
            borderTopRightRadius,
            borderBottomLeftRadius,
            borderBottomRightRadius,
            // width: width !== undefined ? width - 2 : undefined,
            backgroundColor,
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            alignSelf: 'stretch',
          },
          compact
            ? {
                height:
                  themeConfig.heightCompact !== undefined
                    ? themeConfig.heightCompact - 2
                    : undefined,
                paddingLeft:
                  paddingLeft !== undefined
                    ? paddingLeft
                    : themeConfig.paddingLeft / 2,
                paddingRight:
                  paddingRight !== undefined
                    ? paddingRight
                    : themeConfig.paddingRight / 2,
              }
            : {
                height: themeConfig.height,
                paddingLeft:
                  paddingLeft !== undefined
                    ? paddingLeft
                    : themeConfig.paddingLeft,
                paddingRight:
                  paddingRight !== undefined
                    ? paddingRight
                    : themeConfig.paddingRight,
              },
        ]}
        hoveredStyle={[
          { backgroundColor: backgroundColorHovered },
          styleHovered,
        ]}
        pressedStyle={[
          { backgroundColor: backgroundColorPressed },
          { opacity: 0.5 },
          stylePressed,
        ]}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
      >
        {rbs && (
          <View
            style={{
              backgroundColor: theme.colors.support30,
              width: 4,
              height: 4,
              borderRadius: 2,
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 9,
            }}
          />
        )}
        {icon && (
          <Icon
            source={icon}
            size={iconSize}
            color={incomingIconColor ?? iconColor}
            style={iconStyle}
          />
        )}
        {!!buttonText && (
          <Txt
            adjustsFontSizeToFit={adjustsFontSizeToFit}
            size={textSize}
            weight={textWeight}
            style={[
              compact ? styles.textCompact : styles.text,
              themeConfig.adjustLineHeight
                ? compact
                  ? styles.textCompactLineHeight
                  : styles.textLineHeight
                : undefined,
              icon ? styles.textWithIcon : null,
              uppercase ? { textTransform: 'uppercase' } : undefined,
              {
                color: textColor,
              },
              textStyle,
            ]}
            numberOfLines={numberOfLines}
          >
            {buttonText}
          </Txt>
        )}
        {iconRight && (
          <Icon
            source={iconRight}
            size={iconRightSize || 19}
            color={iconRightColor ?? iconColor}
            style={[
              { marginLeft: theme.paddingHorizontalHalf },
              iconRightStyle,
            ]}
          />
        )}
        {showBadge && (
          <View
            style={{
              position: 'absolute',
              right: 2,
              bottom: 2,
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: theme.colors.orange,
              zIndex: 1,
              elevation: 1,
            }}
          />
        )}
      </Pressable>
    </Glass>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  buttonPressable: {
    alignItems: 'center',
    ...Platform.select({
      web: { cursor: 'pointer' },
    }),
  },

  buttonChipLike: {
    borderRadius: 99,
  },
  buttonChipLikeCompact: {
    borderRadius: 99,
  },
  disabled: {
    opacity: 0.42,
  },
  text: {
    textAlign: 'center',
  },
  textCompact: {
    textAlign: 'center',
    fontSize: TEXT_SIZE_XSMALL,
  },
  textLineHeight: {
    includeFontPadding: false,
  },
  textCompactLineHeight: {
    includeFontPadding: false,
  },
  textWithIcon: {
    marginLeft: theme.grid / 2,
  },
});
